<!-- Component can be used in microscope and periscope -->
<template>
  <button
    :type="type"
    :disabled="disabled"
    :data-color="color"
    :data-textcolor="textColor"
    :class="{
      disabled,
      fab,
      large,
      medium,
      small,
      icon,
      block,
      loading,
    }"
    @click="onClick"
  >
    <span v-if="$slots.prepend" class="prepend">
      <slot name="prepend" />
    </span>

    <span class="btn-text">
      <slot />
    </span>

    <span v-if="$slots.append" class="append">
      <slot name="append" />
    </span>

    <slot name="badge" />
  </button>
</template>

<script setup lang="ts">
const emit = defineEmits<{
  (e: 'click', event?: Event): void
}>()

const props = withDefaults(
  defineProps<{
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    color?: 'blue' | 'green' | 'red' | 'transparent'
    textColor?: 'white' | 'default'
    fab?: boolean
    large?: boolean
    medium?: boolean
    small?: boolean
    icon?: boolean

    block?: boolean
  }>(),
  {
    type: 'submit',
  },
)

const onClick = (event: Event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style scoped lang="scss">
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

button {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-fg-default);
  position: relative;

  background-color: var(--color-button-default-bg);
  color: var(--color-fg-default);

  // FAB button, only icon
  &.fab {
    width: 56px;
    height: 56px;
    padding: 0;
    border-radius: 50%;
    justify-content: center;
    gap: 0;

    .btn-text {
      width: auto;
    }
  }

  &.fab.small {
    width: 30px;
    height: 30px;
  }

  &.fab.medium {
    width: 48px;
    height: 48px;
  }

  &.fab.large {
    width: 56px;
    height: 56px;
  }

  &.fab.icon {
    padding: 0 !important;
  }
  // -------

  .btn-text {
    width: 100%;
    text-align: center;
  }

  .prepend,
  .append {
    :deep(svg) {
      vertical-align: top;
    }
  }

  &.large {
    padding: 10px 22px;
  }

  &.medium {
    padding: 8px 12px;
  }

  &.small {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 4px;
  }

  &.icon {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px !important;
    border-radius: max(44px, 25%);

    > span {
      display: flex;
      align-items: center;
    }

    &.primary.outlined:hover {
      color: white;
    }

    :deep(svg) {
      vertical-align: top;
    }
  }

  &.block {
    width: 100%;
  }

  &:hover {
    background-color: var(--color-button-default-hover-bg);
  }

  &:active {
    background-color: var(--color-button-default-active-bg);
  }

  &.loading {
    pointer-events: none;

    // Any colour variant has a solid dark fill, so the spinner is inverted to
    // stay visible on it.
    &[data-color]::after {
      border-color: var(--color-button-spinner-inverted);
      border-top-color: var(--color-button-spinner-inverted-top);
    }

    &::after {
      content: '';
      display: inline-block;
      animation: spin 0.8s infinite linear;
      border: 2px solid var(--color-button-spinner-default);
      border-top-color: var(--color-button-spinner-default-top);
      border-radius: 50%;
      margin: auto;
      width: 12px;
      height: 12px;
      position: absolute;
      inset: 0;
    }

    .btn-text {
      opacity: 0;
    }
  }

  &[data-color='blue'] {
    background-color: var(--color-button-primary-bg);
    color: white;

    &:hover {
      background-color: var(--color-button-primary-hover-bg);
    }

    &:active {
      background-color: var(--color-button-primary-active-bg);
    }
  }

  &[data-color='green'] {
    background-color: var(--color-button-success-bg);
    color: white;

    &:hover {
      background-color: var(--color-button-success-hover-bg);
    }

    &:active {
      background-color: var(--color-button-success-active-bg);
    }
  }

  &[data-color='red'] {
    background-color: var(--color-button-danger-bg);
    color: white;

    &:hover {
      background-color: var(--color-button-danger-hover-bg);
    }

    &:active {
      background-color: var(--color-button-danger-active-bg);
    }
  }

  &[data-color='transparent'] {
    &[data-textcolor='white'] {
      color: white;
    }

    &[data-textcolor='default'] {
      color: var(--color-fg-default);
    }

    background-color: transparent;

    &:hover {
      background-color: var(--color-button-transparent-bg);
    }
  }

  &:disabled {
    pointer-events: none;
    background-color: var(--color-button-default-bg) !important;
    color: var(--color-button-disabled-fg) !important;
  }
}
</style>
